import { Box, FormControl, MenuItem, Select, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import type { ExtractedQuestion, QuestionRegion } from '@/types/pipeline';
import { getQuestionRubricAccentColor, HIGHLIGHT_STROKE } from '@/lib/regionColors';
import { QUESTION_TYPE_LABELS, getQuestionTypeLabel } from '@/lib/questionTypeLabels';
import { EssayQuestionConfirm } from '@/components/EssayQuestionConfirm';

interface QuestionListProps {
  questions: ExtractedQuestion[];
  allRegions: QuestionRegion[];
  focusedQuestionId: string | null;
  onQuestionHoverEnter: (questionId: string) => void;
  onQuestionHoverLeave: () => void;
  renderQuestionContent?: (question: ExtractedQuestion, isFocused: boolean, boxAccent: string | null) => React.ReactNode;
  maxHeight?: number | string;
  /** When provided, replaces the static type chip with an editable dropdown */
  onQuestionTypeChange?: (questionId: string, newType: ExtractedQuestion['questionType']) => void;
  /** Set of questionIds whose type was manually overridden */
  manuallyChangedIds?: Set<string>;
  /** When provided, essay questions show a confirm/edit UI for their topic content */
  onQuestionContentChange?: (questionId: string, content: string) => void;
}

export function QuestionList({
  questions,
  allRegions,
  focusedQuestionId,
  onQuestionHoverEnter,
  onQuestionHoverLeave,
  renderQuestionContent,
  maxHeight = 600,
  onQuestionTypeChange,
  manuallyChangedIds,
  onQuestionContentChange,
}: QuestionListProps) {
  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        maxHeight,
        overflow: 'auto',
      }}
    >
      {questions.map((question, i) => {
        const isFocused = focusedQuestionId === question.questionId;
        const regionsTyped = allRegions as QuestionRegion[];
        const boxAccent = getQuestionRubricAccentColor(regionsTyped, question.questionId);
        const strokeColor = isFocused ? HIGHLIGHT_STROKE : boxAccent;
        const rowBg = isFocused
          ? alpha(HIGHLIGHT_STROKE, 0.14)
          : boxAccent != null
            ? alpha(boxAccent, 0.09)
            : 'background.paper';
        const isManuallyChanged = manuallyChangedIds?.has(question.questionId) ?? false;

        return (
          <Box
            key={question.questionId}
            onMouseEnter={() => onQuestionHoverEnter(question.questionId)}
            onMouseLeave={onQuestionHoverLeave}
            sx={{
              p: 1.5,
              pl: 1.25,
              borderBottom: i < questions.length - 1 ? '1px solid' : 'none',
              borderColor: 'divider',
              borderLeft: '4px solid',
              borderLeftColor: strokeColor ?? 'transparent',
              bgcolor: rowBg,
              transition: 'background-color 0.15s, border-color 0.15s, outline 0.15s',
              cursor: 'default',
              outline: isFocused ? `2px solid ${HIGHLIGHT_STROKE}` : 'none',
              outlineOffset: '-2px',
            }}
          >
            {renderQuestionContent ? (
              renderQuestionContent(question, isFocused, boxAccent)
            ) : (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                  <Typography
                    variant="subtitle2"
                    fontWeight={700}
                    sx={{ color: isFocused ? HIGHLIGHT_STROKE : boxAccent ?? 'primary.main' }}
                  >
                    {question.questionId}
                  </Typography>

                  {onQuestionTypeChange ? (
                    <>
                      <FormControl size="small">
                        <Select
                          value={question.questionType}
                          onChange={(e) =>
                            onQuestionTypeChange(
                              question.questionId,
                              e.target.value as ExtractedQuestion['questionType'],
                            )
                          }
                          sx={{
                            fontSize: '0.75rem',
                            minWidth: 136,
                            color: isFocused ? HIGHLIGHT_STROKE : boxAccent ?? undefined,
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: isFocused
                                ? HIGHLIGHT_STROKE
                                : boxAccent ?? undefined,
                            },
                          }}
                        >
                          {Object.entries(QUESTION_TYPE_LABELS).map(([value, label]) => (
                            <MenuItem key={value} value={value} sx={{ fontSize: '0.8125rem' }}>
                              {label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      {isManuallyChanged && (
                        <Typography variant="caption" color="warning.main" sx={{ whiteSpace: 'nowrap' }}>
                          已手動修改
                        </Typography>
                      )}
                    </>
                  ) : (
                    <Box
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        px: 1,
                        py: 0.25,
                        border: '1px solid',
                        borderColor: isFocused
                          ? HIGHLIGHT_STROKE
                          : boxAccent ?? 'divider',
                        borderRadius: '16px',
                        fontSize: '0.6875rem',
                        color: isFocused ? HIGHLIGHT_STROKE : boxAccent ?? 'text.secondary',
                      }}
                    >
                      {getQuestionTypeLabel(question.questionType)}
                    </Box>
                  )}
                </Box>

                {question.sectionInstruction && (
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                    {question.sectionInstruction}
                  </Typography>
                )}
                {question.questionType === 'essay' && onQuestionContentChange ? (
                  <EssayQuestionConfirm
                    question={question}
                    onConfirm={(confirmed) => onQuestionContentChange(confirmed.questionId, confirmed.questionText)}
                  />
                ) : (
                  <>
                    <Typography variant="body2" color="text.secondary">
                      {question.questionText}
                    </Typography>
                    {question.studentAnswer && (
                      <Typography variant="caption" display="block" sx={{ mt: 0.5, color: 'success.main' }}>
                        作答：{question.studentAnswer}
                      </Typography>
                    )}
                  </>
                )}
              </>
            )}
          </Box>
        );
      })}
    </Box>
  );
}
